// src/components/Footer.tsx
// Site footer.
//
//   <footer>
//     Inner -> Columns
//       BrandColumn  : name, positioning, description, focus, social links
//       NavColumn    : <nav aria-label="Footer"> -> real routes
//       ConnectColumn: label, lead, Query CTA
//     Base -> copyright + attribution
//
// Design notes:
//
// - IDENTITY, NOT A SECOND HERO. The heading is the person's name at 1.5rem,
//   not a display type size. A footer that shouts competes with the page it
//   closes; this one reads as a sign-off.
//
// - THE SOCIAL LINKS LIVE ONCE. The previous version rendered its own
//   GitHub/LinkedIn/Email row *and* mounted <ContactActions>, which draws the
//   same three plus a phone number from siteConfig -- seven near-identical
//   controls in two icon styles (react-icons Fa* against lucide) at two sizes.
//   The icons stay here, in the brand column, and ContactActions stays on
//   /contacts and /get-in-touch where it is the primary action set.
//
// - SITE CONFIG IS THE ONLY SOURCE OF IDENTITY AND URLS. The name comes from
//   siteConfig.name and every link from siteConfig.socialLinks, so nothing here
//   duplicates a profile. siteConfig.twitter is a bare "https://twitter.com/"
//   placeholder with no account behind it, so it is deliberately not rendered.
//
// - POSITIONING IS NOT INVENTED. The role line matches the one in the Navbar
//   and Home page, and the copy only describes the documented capability set:
//   services and APIs, integrations, data processing, and enough front-end to
//   integrate against the contract.
//
// - THE CTA OPENS THE QUERY WINDOW, IT DOES NOT NAVIGATE. It calls the same
//   context handler the Navbar uses, so a visitor can send a query from the
//   bottom of any page without losing their place. The /query route still
//   renders the form as a standalone page.
//
// - "Contact" IS NOT A ROUTE EITHER. It opens the contact window, so it is
//   rendered as a <button> in the same list as the links rather than a fourth
//   <Link>. That is the one entry in the column that is an action, and it is
//   marked as such in the markup.
//
// - LINKS ARE REAL ROUTES. They were href="#home" / "#about" / "#projects" /
//   "#contact" before; three of the four matched nothing on the page.
import type { ReactElement } from 'react';
import { useReducedMotion } from 'framer-motion';
import { FaEnvelope, FaGithub, FaLinkedin } from 'react-icons/fa';

import { siteConfig } from '../config/siteConfig';
import { useQueryModal } from '../context/QueryModalContext';
import { useContactModal } from '../context/ContactModalContext';
import { STAGGER, motionSet } from './about/motion';
import {
  Attribution,
  BaseInner,
  BrandColumn,
  Columns,
  ConnectColumn,
  ConnectCta,
  ConnectLead,
  Copyright,
  Description,
  FocusLine,
  FooterBase,
  FooterInner,
  FooterRoot,
  Name,
  NavAction,
  NavColumn,
  NavLink,
  NavList,
  Position,
  SocialLink,
  SocialList,
} from './Footer.styles';

/** Same line the Navbar brand and the Home hero use. */
const POSITION = 'Backend Engineer · Enterprise Application Development';

/** A domain list, not a technology list. */
const FOCUS = 'Backend systems • APIs • Integrations • Enterprise Applications';

const DESCRIPTION =
  'Backend engineering for enterprise applications: services and APIs, system ' +
  'integrations, and the data processing behind them. Enough front-end to build ' +
  'against the contract and integrate alongside the back-end.';

/** Declared once so the nav list and the markup cannot drift apart. */
const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Projects', to: '/projects' },
] as const;

/** Not a route: rendered as a button that opens the contact window. */
const CONTACT_LABEL = 'Contact';

interface SocialLinkConfig {
  href: string;
  label: string;
  icon: ReactElement;
  /** The mailto link must not open a new tab; a blank target there is a bug. */
  external: boolean;
}

const SOCIAL_LINKS: readonly SocialLinkConfig[] = [
  {
    href: siteConfig.socialLinks.github,
    label: 'GitHub',
    icon: <FaGithub />,
    external: true,
  },
  {
    href: siteConfig.socialLinks.linkedin,
    label: 'LinkedIn',
    icon: <FaLinkedin />,
    external: true,
  },
  {
    href: siteConfig.socialLinks.email,
    label: 'Email',
    icon: <FaEnvelope />,
    external: false,
  },
];

const Footer = () => {
  const { openQueryModal } = useQueryModal();
  const { openContactModal } = useContactModal();
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);

  const year = new Date().getFullYear();

  return (
    <FooterRoot>
      <FooterInner>
        {/* One restrained group reveal. The footer is the last thing on the
            page, so animating each control individually would read as noise. */}
        <Columns
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <BrandColumn variants={anim.reveal}>
            <Name>{siteConfig.name}</Name>
            <Position>{POSITION}</Position>
            <Description>{DESCRIPTION}</Description>
            <FocusLine>{FOCUS}</FocusLine>

            <SocialList>
              {SOCIAL_LINKS.map(({ href, label, icon, external }) => (
                <li key={label}>
                  <SocialLink
                    href={href}
                    aria-label={label}
                    title={label}
                    {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  >
                    <span aria-hidden="true">{icon}</span>
                  </SocialLink>
                </li>
              ))}
            </SocialList>
          </BrandColumn>

          <NavColumn variants={anim.reveal} aria-label="Footer">
            <Position>Navigation</Position>
            <NavList>
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to}>{item.label}</NavLink>
                </li>
              ))}

              {/* The one action in the navigation column. It opens the contact
                  window over whatever page the visitor is on, so it does not
                  navigate and carries no href. */}
              <li>
                <NavAction type="button" aria-haspopup="dialog" onClick={openContactModal}>
                  {CONTACT_LABEL}
                </NavAction>
              </li>
            </NavList>
          </NavColumn>

          <ConnectColumn variants={anim.reveal}>
            <Position>Enquiries</Position>
            <ConnectLead>
              Have a project or a question in mind? Send a query and it opens
              here, without sending you off this page.
            </ConnectLead>
            <ConnectCta label="Start a Conversation" onClick={openQueryModal} />
          </ConnectColumn>
        </Columns>
      </FooterInner>

      <FooterBase>
        <BaseInner>
          <Copyright>
            &copy; {year} {siteConfig.name}. All rights reserved.
          </Copyright>
          <Attribution>Built with React · TypeScript · Styled Components</Attribution>
        </BaseInner>
      </FooterBase>
    </FooterRoot>
  );
};

export default Footer;
